import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Mail, Gift } from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const NewsletterPopup = () => {
  const { language } = useStore();
  const location = useLocation();
  const t = translations[language];
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Don't show popup on admin pages
    if (location.pathname.startsWith('/admin')) {
      return;
    }
    
    // Check if popup was already shown
    const popupShown = localStorage.getItem('ebb_newsletter_popup_shown');
    if (!popupShown) {
      // Show popup after 10 seconds
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('ebb_newsletter_popup_shown', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language }),
      });

      const data = await response.json();

      if (data.already_subscribed) {
        setError(language === 'fr' ? 'Vous \u00eates d\u00e9j\u00e0 inscrit !' : 'U bent al ingeschreven!');
      } else {
        setSuccess(true);
        localStorage.setItem('ebb_newsletter_popup_shown', 'true');
        setTimeout(() => {
          setIsOpen(false);
        }, 3000);
      }
    } catch (err) {
      setError(language === 'fr' ? 'Une erreur est survenue' : 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden animate-slideUp">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Image Header */}
        <div className="h-32 bg-gradient-to-br from-amber-700 to-amber-900 flex items-center justify-center">
          <div className="text-center text-white">
            <Gift className="w-12 h-12 mx-auto mb-2" />
            <p className="text-lg font-semibold">
              {language === 'fr' ? '-10% sur votre premi\u00e8re commande' : '-10% op uw eerste bestelling'}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {language === 'fr' ? 'Merci !' : 'Bedankt!'}
              </h3>
              <p className="text-gray-600">
                {language === 'fr'
                  ? 'Vous recevrez bient\u00f4t nos offres exclusives.'
                  : 'U ontvangt binnenkort onze exclusieve aanbiedingen.'}
              </p>
            </div>
          ) : (
            <>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                {language === 'fr' ? 'Rejoignez la communaut\u00e9 EBB' : 'Word lid van de EBB community'}
              </h3>
              <p className="text-gray-600 text-center mb-6">
                {language === 'fr'
                  ? 'Inscrivez-vous pour recevoir nos offres exclusives et les derni\u00e8res nouveaut\u00e9s.'
                  : 'Schrijf u in om onze exclusieve aanbiedingen en laatste nieuwtjes te ontvangen.'}
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm text-center">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.footer.newsletterPlaceholder}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-amber-700 hover:bg-amber-800 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap"
                  >
                    {loading ? '...' : t.footer.subscribe}
                  </button>
                </div>
              </form>

              <p className="text-xs text-gray-400 text-center mt-4">
                {language === 'fr'
                  ? 'En vous inscrivant, vous acceptez notre politique de confidentialit\u00e9.'
                  : 'Door u in te schrijven, gaat u akkoord met ons privacybeleid.'}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsletterPopup;
